import { isClient } from './device-utils';

/**
 * A functions collection to manage DOM IntersectionObserver
 * @packageDocumentation
 * @module utils/intersect
 */

const DEFAULT_VIEWPORT_HEIGHT = 1000;

/**
 * Creates DOM IntersectionObserver
 * @param {string} targetsSelector - The Query Selector to find DOM nodes
 * @returns {function} - Returns the callback function
 */
function intersect(targetsSelector: string): any {
  const visibleElements: number[] = [];
  let activeIndex: number;

  return (externalCallback: Function): any => {
    const targets = document.querySelectorAll(targetsSelector);
    if (!targets.length) {
      return;
    }

    targets.forEach((target, i) => target.setAttribute('data-index', `${i}`));

    const threshold = calcThreshold(targets);
    const callback: IntersectionObserverCallback = (entries: IntersectionObserverEntry[]) =>
      entries.forEach(setActiveEntry);
    const options = {
      root: null,
      rootMargin: '0px',
      threshold,
    };
    const observer = new IntersectionObserver(callback, options);
    targets.forEach((target) => observer.observe(target));

    function setActiveEntry(entry: IntersectionObserverEntry) {
      const target = entry.target as HTMLElement;
      const itemIndex = Number(target.dataset.index);
      const visibleIndex = visibleElements.indexOf(itemIndex);
      const isEntryInvisible = visibleIndex === -1;
      const isEntryIntersected = entry.intersectionRatio >= threshold;

      if (isEntryIntersected && isEntryInvisible) {
        visibleElements.push(itemIndex);
      } else if (!isEntryIntersected && !isEntryInvisible) {
        visibleElements.splice(visibleIndex, 1);
      }

      if (!visibleElements.length) {
        return;
      }

      visibleElements.sort((a, b) => a - b);
      const currentIndex = visibleElements[0];

      if (currentIndex === activeIndex) {
        return;
      }

      activeIndex = currentIndex;
      const activeItem = document.querySelector(`${targetsSelector}[data-index="${activeIndex}"]`);
      externalCallback(activeItem);
    }
    return () => observer.disconnect();
  };
}

function calcThreshold(elements: NodeListOf<Element>) {
  const heightAverage = calcMaxHeight(elements);
  const offset = 150;
  const viewportHeight = isClient() ? window.innerHeight - offset : DEFAULT_VIEWPORT_HEIGHT;
  let threshold = 0.9;
  if (heightAverage > viewportHeight) {
    threshold = viewportHeight / heightAverage;
    threshold = Math.round(threshold * 100) / 100;
  }

  return threshold;
}

function calcMaxHeight(elements: NodeListOf<Element>) {
  const heights: number[] = [];
  elements.forEach((element) => {
    const size = element.getBoundingClientRect();
    heights.push(size.height);
  });

  return Math.max(...heights);
}

export default intersect;
